import React, { useMemo, useState } from 'react';
import { getCsrfToken as csrfToken } from '../../../lib/csrf.js';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

const TYPES = [
  { value: 'bundle',            label: 'LearnWorlds Bundle',           hint: 'Enroll user in every course in this bundle.' },
  { value: 'subscription_plan', label: 'LearnWorlds Subscription Plan', hint: 'Enroll user in every course in this plan.' },
  { value: 'courses',           label: 'Custom course list',            hint: 'Hand-pick the exact courses for this license.' },
];

function formatAmount(cents, currency) {
  if (cents == null) return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

export default function LicenseMappingsEdit({ license, catalog, stripe, catalog_error: catalogError }) {
  const initialType = license.mapping?.type || 'bundle';
  const [type, setType] = useState(TYPES.find((t) => t.value === initialType) ? initialType : 'bundle');
  const [resourceId, setResourceId] = useState(license.mapping?.id || '');
  const [courseIds, setCourseIds] = useState(license.mapping?.ids || []);
  const [courseFilter, setCourseFilter] = useState('');
  const [complianceText, setComplianceText] = useState(license.compliance_text || '');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSyncStripe = (e) => {
    e.preventDefault();
    if (syncing) return;
    if (!window.confirm('Sync Stripe pricing? This creates/updates products and prices in Stripe for every license.')) return;
    setSyncing(true);
    router.post('/superadmin/license_mappings/sync_stripe_pricing', {}, {
      headers: { 'X-CSRF-Token': csrfToken() },
      onFinish: () => setSyncing(false),
    });
  };

  const priceLabel = stripe ? formatAmount(stripe.amount_cents, stripe.currency) : null;

  const filteredCourses = useMemo(() => {
    const q = courseFilter.trim().toLowerCase();
    return catalog.courses.filter((c) => !q || c.title.toLowerCase().includes(q));
  }, [catalog.courses, courseFilter]);

  const toggleCourse = (id) => {
    setCourseIds((prev) => prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload = { mapping: { type }, compliance_text: complianceText };
    if (type === 'courses') {
      payload.mapping.course_ids = courseIds;
    } else {
      payload.mapping.id = resourceId;
    }
    router.patch(`/superadmin/license_mappings/${license.id}`, payload, {
      headers: { 'X-CSRF-Token': csrfToken() },
      onFinish: () => setSaving(false),
    });
  };

  const sortedBundles = useMemo(() => [...catalog.bundles].sort((a, b) => a.title.localeCompare(b.title)), [catalog.bundles]);
  const sortedPlans = useMemo(() => [...catalog.subscription_plans].sort((a, b) => a.title.localeCompare(b.title)), [catalog.subscription_plans]);

  return (
    <Layout>
      <div className="px-6 py-6 max-w-4xl">
        <div className="mb-4">
          <Link href="/superadmin/license_mappings" className="text-sm text-indigo-600 hover:text-indigo-700">
            ← All mappings
          </Link>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">{license.name}</h1>
        <p className="mt-1 text-sm text-gray-600">
          {license.facility_type} · {license.student_type} · {license.billing_model} · {license.license_type}
        </p>

        {catalogError && (
          <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {catalogError}
          </div>
        )}

        {stripe && (
          <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-gray-900">Stripe product</h2>
                  {stripe.test_mode && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200">
                      Test mode
                    </span>
                  )}
                </div>
                <div className="mt-2 text-sm text-gray-800">
                  {stripe.product_name || <span className="text-gray-400 italic">Product name unavailable</span>}
                </div>
                <div className="mt-1 text-xs text-gray-600">
                  {priceLabel && <span className="font-medium">{priceLabel}</span>}
                  {priceLabel && stripe.recurring && <span> / {stripe.interval || 'recurring'}</span>}
                  {priceLabel && !stripe.recurring && <span> · one-time</span>}
                </div>
                <div className="mt-2 space-y-0.5 text-xs font-mono text-gray-500">
                  <div>Price: {stripe.price_id}</div>
                  {stripe.product_id && <div>Product: {stripe.product_id}</div>}
                </div>
                <div className="mt-3 flex flex-wrap gap-3 text-xs">
                  <a
                    href={stripe.price_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Open price in Stripe ↗
                  </a>
                  {stripe.product_url && (
                    <a
                      href={stripe.product_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      Open product in Stripe ↗
                    </a>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleSyncStripe}
                disabled={syncing}
                className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
                title="Runs the canonical pricing sync against Stripe for every license."
              >
                {syncing ? 'Syncing…' : 'Sync Stripe pricing'}
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-6 rounded-lg border border-gray-200 bg-white p-6">
          {license.live_only && (
            <div className="rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
              <div className="font-semibold">Live-only SKU — no LearnWorlds mapping needed.</div>
              <div className="mt-1 text-indigo-800">
                Purchasers of this license attend live webinars for their hours; no self-paced LearnWorlds seat is granted. The mapping fields below are hidden and any saved mapping is ignored at purchase time.
              </div>
            </div>
          )}

          <fieldset className={license.live_only ? 'hidden' : ''}>
            <legend className="text-sm font-semibold text-gray-900 mb-2">Mapping type</legend>
            <div className="space-y-2">
              {TYPES.map((t) => (
                <label key={t.value} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    value={t.value}
                    checked={type === t.value}
                    onChange={() => setType(t.value)}
                    className="mt-1 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{t.label}</div>
                    <div className="text-xs text-gray-500">{t.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>

          {!license.live_only && type === 'bundle' && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Bundle</label>
              <select
                value={resourceId}
                onChange={(e) => setResourceId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
              >
                <option value="">— Select a bundle —</option>
                {sortedBundles.map((b) => (
                  <option key={b.id} value={b.id}>{b.title} ({b.course_count} courses)</option>
                ))}
              </select>
            </div>
          )}

          {!license.live_only && type === 'subscription_plan' && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Subscription plan</label>
              <select
                value={resourceId}
                onChange={(e) => setResourceId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
              >
                <option value="">— Select a plan —</option>
                {sortedPlans.map((p) => (
                  <option key={p.id} value={p.id}>{p.title} ({p.course_count} courses)</option>
                ))}
              </select>
            </div>
          )}

          {!license.live_only && type === 'courses' && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">
                Courses ({courseIds.length} selected)
              </label>
              <input
                type="text"
                placeholder="Filter courses…"
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm mb-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
              />
              <div className="max-h-96 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 divide-y divide-gray-100">
                {filteredCourses.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-white cursor-pointer">
                    <input
                      type="checkbox"
                      checked={courseIds.includes(c.id)}
                      onChange={() => toggleCourse(c.id)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-800 flex-1">{c.title}</span>
                    <span className="text-xs font-mono text-gray-400">{c.id}</span>
                  </label>
                ))}
                {filteredCourses.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-gray-500">No courses match.</div>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1">Compliance text</label>
            <p className="text-xs text-gray-500 mb-2">
              Shown beneath the plan cards on the purchase page. Leave blank to hide the line.
            </p>
            <textarea
              value={complianceText}
              onChange={(e) => setComplianceText(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Includes all required self-paced courses, certificates, and compliance tracking."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{complianceText.length} / 500</p>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Link
              href="/superadmin/license_mappings"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 cursor-pointer"
            >
              {saving ? 'Saving…' : 'Save mapping'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
